import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { LoadingView } from '../../../common/views/LoadingView';
import { CatalogMergeOption } from '../../../../redux/api/fymAPI.types';
import { mergeSections } from './mergeRules';
import { mergeMessages } from './mergeMessages';
import { MergeOptionRow } from './MergeOptionRow';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the merge dialogs; each part uses its own slice of it */
import styles from './Merge.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface MergePickStepProps {
  candidates: CatalogMergeOption[] | undefined;
  isLoading: boolean;
  selectedUuid: string | undefined;
  onSelect: (uuid: string) => void;
}

/** The picker body (design catalog-merge a2): a search, the likely duplicates, then the other songs. */
const MergePickStepComponent: React.FC<MergePickStepProps> = ({ candidates, isLoading, selectedUuid, onSelect }) => {
  const intl = useIntl();
  const [search, setSearch] = useState('');
  const onSearch = useCallback((event: React.ChangeEvent<HTMLInputElement>) => setSearch(event.target.value), []);
  const sections = useMemo(() => mergeSections(candidates ?? [], search), [candidates, search]);

  if (isLoading || candidates === undefined) {
    return <LoadingView />;
  }
  if (candidates.length === 0) {
    return <p className={styles.empty}>{intl.formatMessage(mergeMessages.pickNone)}</p>;
  }
  const groups = [
    { key: 'likely', label: mergeMessages.pickLikely, rows: sections.likely },
    { key: 'others', label: mergeMessages.pickOthers, rows: sections.others },
  ].filter((group) => group.rows.length > 0);

  return (
    <div className={styles.stack}>
      <input
        type="search"
        className={styles.search}
        value={search}
        onChange={onSearch}
        placeholder={intl.formatMessage(mergeMessages.pickSearch)}
        aria-label={intl.formatMessage(mergeMessages.pickSearch)}
      />
      {groups.length === 0 && <p className={styles.empty}>{intl.formatMessage(mergeMessages.pickNoMatch)}</p>}
      {groups.map((group) => (
        <div key={group.key} className={styles.stack} role="radiogroup" aria-label={intl.formatMessage(group.label)}>
          <span className={styles.group}>{intl.formatMessage(group.label)}</span>
          <div className={styles.list}>
            {group.rows.map((option) => (
              <MergeOptionRow
                key={option.uuid}
                option={option}
                isSelected={option.uuid === selectedUuid}
                onSelect={onSelect}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export const MergePickStep = TypedMemo(MergePickStepComponent);
